import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import ProductModal from '@features/trend-research/components/common/ProductModal';
import AddSectionModal from '@features/trend-research/components/moodboard/components/AddSectionModal';
import CreateOrUpdateMoodboard from '@features/trend-research/components/moodboard/components/CreateOrUpdateMoodboard';
import MoodBoardSections from '@features/trend-research/components/moodboard/components/MoodBoardSections';
import MoodBoardTagsWrapper from '@features/trend-research/components/moodboard/components/MoodBoardTagsWrapper';
import { useFindMoodBoardByIdQuery } from '@features/trend-research/api/moodboardApi';
import Tags from '@features/trend-research/components/common/Tags';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { setSystemUserPreferences } from '@services/commonService';
import { downloadMoodBoardFile, removeProductFromMoodBoard } from '@services/trendResearch';

import { downloadBlobFIle, getUserPreference, randomShuffle } from '@utils/Util';

const LAYOUT_CLASSES = ['default-column', 'moodboard-masonry-column-5', 'moodboard-masonry-column-7'];

const MoodBoardDetails = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [showModal, setShowModal] = useState(false);
	const [moodBoardInfo, setMoodBoardInfo] = useState({});
	const [reshuffleType, setReshuffleType] = useState('');
	const [activeLayoutClass, setActiveLayoutClass] = useState('');
	const [isShowAddSection, setIsShowAddSection] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [isShowUpdateModal, setIsShowUpdateModal] = useState(false);
	const [selectedProductSource, setSelectedProductSource] = useState(null);
	const { data, refetch } = useFindMoodBoardByIdQuery(`/${id}`);

	useEffect(() => {
		if (data) {
			setMoodBoardInfo(JSON.parse(JSON.stringify(data)));
		}
	}, [data]);

	const onClose = () => {
		setShowModal(false);
		setSelectedProductId(null);
		setSelectedProductSource(null);
	};

	useEffect(() => {
		const userPreference = getUserPreference();
		setReshuffleType(userPreference?.moodBoardReshuffleType || 'CHANGE_LAYOUT');
		setActiveLayoutClass(userPreference?.moodBoardLayoutClass || '');
		refetch();
	}, []);

	const onCloseCreateModal = () => {
		setIsShowUpdateModal(false);
	};

	const onCloseSectionModal = () => {
		setIsShowAddSection(false);
	};

	const handleReshuffle = () => {
		setReshuffleType(reshuffleType === 'CHANGE_LAYOUT' ? 'REORGANIZE_DATA' : 'CHANGE_LAYOUT');
	};

	useEffect(() => {
		if (reshuffleType !== '') {
			if (reshuffleType === 'CHANGE_LAYOUT') {
				let currentLayoutClassIndex = LAYOUT_CLASSES.findIndex((item) => item === activeLayoutClass);
				let nextLayoutClassIdx =
					currentLayoutClassIndex === LAYOUT_CLASSES.length - 1 ? 0 : currentLayoutClassIndex + 1;
				setActiveLayoutClass(LAYOUT_CLASSES[nextLayoutClassIdx]);
			} else {
				if (moodBoardInfo?.trendMoodBoardProductResponseList) {
					let reshuffleImageList = randomShuffle([
						...moodBoardInfo.trendMoodBoardProductResponseList
					]);
					setMoodBoardInfo((prev) => ({
						...prev,
						trendMoodBoardProductResponseList: reshuffleImageList
					}));
				}
			}
			(async () => {
				dispatch(openLoader());
				await setSystemUserPreferences({
					moodBoardReshuffleType: reshuffleType,
					moodBoardLayoutClass: activeLayoutClass
				});
				dispatch(closeLoader());
			})();
		}
	}, [reshuffleType]);

	const removeProduct = async (productId) => {
		try {
			dispatch(openLoader());
			await removeProductFromMoodBoard(productId);
			refetch();
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const downloadFile = async () => {
		try {
			dispatch(openLoader());
			const response = await downloadMoodBoardFile(id);
			downloadBlobFIle(response);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	return (
		<>
			<div className="top-header ">
				<div className="filter-container-top bg-white">
					<div className="back-button cursor-pointer" onClick={() => navigate(-1)}>
						<img src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`} alt="back-icon" />
					</div>

					<div className="filter-actions moodboard-filter-actions w-auto">
						<button className="button size32 outline gray_dark_01" onClick={handleReshuffle}>
							<img src={`${process.env.PUBLIC_URL}/icons/refresh.png`} alt="" width={14} />
							<span className="ml-1">Reshuffle</span>{' '}
						</button>
						<button className="button size32 ml-12" onClick={downloadFile}>
							Download
						</button>
					</div>
				</div>
			</div>

			<section className="body-content trend-data-container bg-white">
				<div className="trend-products-layout d-flex flex-wrap">
					<div className="left-content mr-3">
						<div className="about-header pb-3">
							{moodBoardInfo?.brandName && (
								<span className="status radius mb-2">{moodBoardInfo?.brandName}</span>
							)}
							<div className="title-and-edit pt-2 d-flex align-items-start justify-content-between">
								<div>
									<h3 className="title gray_dark_01 weight-700">{moodBoardInfo?.name}</h3>
								</div>
								<div
									className="icon ml-2 cursor-pointer"
									onClick={() => setIsShowUpdateModal(true)}
								>
									<img
										src={`${process.env.PUBLIC_URL}/icons/action-edit.png`}
										width={24}
										alt="edit"
									/>
								</div>
							</div>
							<MoodBoardTagsWrapper
								tagResponseList={moodBoardInfo?.tagResponseList || []}
								callBack={() => refetch()}
							/>
						</div>
						<MoodBoardSections
							sections={moodBoardInfo?.sectionResponseList || []}
							callBack={() => refetch()}
						/>
						<div className="add-new-design">
							<button
								className="button text size32 px-0"
								onClick={() => setIsShowAddSection(true)}
							>
								<img src={`${process.env.PUBLIC_URL}/icons/add-task-icon.svg`} alt="add" />
								<span className="regular-14 gray_dark_02 pl-1">Add Section</span>
							</button>
						</div>
					</div>
					<div className={`moodboard-masonry-container ${activeLayoutClass}`}>
						{moodBoardInfo?.trendMoodBoardProductResponseList?.map((item, idx) => (
							<div
								className="masonry-item cursor-pointer"
								key={`mood_board_image_${idx}`}
								onClick={() => {
									if (item?.brandProductId) {
										setSelectedProductSource('Brand');
										setSelectedProductId(item.brandProductId);
									}
									if (item?.instagramProductId) {
										setSelectedProductSource('Instagram');
										setSelectedProductId(item.instagramProductId);
									}
									setShowModal(true);
								}}
							>
								<img src={item.imageUrl} alt="" />
								{item?.isBestseller && <Tags label="Bestseller" className="yellow-3" />}
								{!item?.isBestseller && item?.isNewArrival && (
									<Tags label="New" className="blue-3" />
								)}
								<div className="card-overly radius-4 d-flex flex-column justify-content-between">
									<div className="logo-and-fab d-flex justify-content-between">
										<div />
										<div className="custom-chekbox">
											<div className="form-group d-flex mb-0">
												<img
													src={`${process.env.PUBLIC_URL}/icons/favorite.svg`}
													alt="not-favourite"
													className="add-to-favorite cursor-pointer"
													onClick={async (e) => {
														e.stopPropagation();
														await removeProduct(item.id);
													}}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
			{isShowUpdateModal && (
				<CreateOrUpdateMoodboard
					moodBoardInfo={moodBoardInfo}
					onClose={onCloseCreateModal}
					callBack={() => refetch()}
					title="Edit"
					type="UPDATE"
				/>
			)}
			{isShowAddSection && (
				<AddSectionModal
					moodBoardInfo={moodBoardInfo}
					onClose={onCloseSectionModal}
					callBack={() => refetch()}
					title="Add New Section"
				/>
			)}
			{showModal && selectedProductId && (
				<ProductModal onClose={onClose} id={selectedProductId} typeOfSource={selectedProductSource} />
			)}
		</>
	);
};

export default MoodBoardDetails;
